import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Page = styled.main`
  background: #ffffff;
  min-height: 100vh;
  padding-top: 80px;
`;

const Container = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
`; 

const ContactSection = styled.div`
  background: transparent;
  border-radius: 12px;
  padding: 2rem 0;
  margin-bottom: 2rem;
  position: relative;
`;

const ContactHeading = styled.h2`
  font-family: 'Great Vibes', 'Betrycia-Regular', cursive;
  font-size: clamp(2.8rem, 6vw, 3.8rem);
  font-weight: 500;
  color: var(--primary-purple);
  text-align: center;
  letter-spacing: 1px;
  margin: 0 0 2rem;
`;

const TopGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FormSection = styled.div`
  background: transparent;
  padding: 0;
`;

const FormWrap = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  display: block;
  color: var(--primary-purple);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 0;
  border: none;
  border-bottom: 2px solid var(--primary-purple);
  background: transparent;
  outline: none;
  font-size: 1rem;
  color: var(--primary-purple);
  transition: all 0.3s ease;
  
  &::placeholder { 
    color: var(--primary-purple);
    opacity: 0.6;
  }
  
  &:focus {
    border-bottom-color: #D4AF37;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.8rem 0;
  border: none;
  border-bottom: 2px solid var(--primary-purple);
  background: transparent;
  outline: none;
  font-size: 1rem;
  color: var(--primary-purple);
  resize: vertical;
  transition: all 0.3s ease;
  font-family: inherit;
  
  &::placeholder { 
    color: var(--primary-purple);
    opacity: 0.6;
  }
  
  &:focus {
    border-bottom-color: #D4AF37;
  }
`;

const SubmitButton = styled.button`
  padding: 1rem 2.5rem;
  background: #D4AF37;
  color: #ffffff;
  border: none;
  border-radius: 0;
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  margin-top: 1rem;
  width: fit-content;
  
  &:hover { 
    background: #c79b2f;
    transform: translateY(-2px);
  }
`;

const InfoSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: var(--primary-purple);
  border-radius: 8px;
  padding: 2rem 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.12);
  
  &:hover {
    transform: translateY(-5px);
    background: #1a1116; /* darker purple on hover */
  }
`;

const IconCircle = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #D4AF37;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.2rem;
  color: #ffffff; /* svg/icon color white */
  
  /* Ensure embedded SVG icons render white */
  svg, svg * {
    fill: #ffffff !important;
    stroke: #ffffff !important;
  }
`;

const CardTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardText = styled.p`
  margin: 0;
  color: #ffffff;
  font-size: 0.9rem;
  line-height: 1.5;
  transition: color 0.2s ease;
  &:hover { color: #D4AF37; }
`;

const LinkText = styled.a`
  color: #ffffff;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover { 
    color: #D4AF37;
  }
`;

const MapSection = styled.section`
  margin-top: 2rem;
`;

const MapFrame = styled.iframe`
  width: 100%;
  height: 420px;
  border: 0;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
`;

const ContactUs: React.FC = () => {
  const [form, setForm] = useState({ firstName: '', address: '', phone: '', message: '' });

  useEffect(() => {
    const elements = document.querySelectorAll('[data-reveal]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target as HTMLElement;
          const dir = el.getAttribute('data-reveal-dir');
          if (dir) { el.setAttribute('data-reveal', dir); }
          el.classList.add('revealed');
        }
      });
    }, { threshold: 0.15 });

    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const phone = '923708025503'; // WhatsApp number without + or spaces
    const message = `Hello Flour & Fancy\nName: ${form.firstName}\nPhone: ${form.phone}\nAddress: ${form.address}\nMessage: ${form.message}`;
  const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <Page>
      <Container>
        <ContactHeading data-reveal="down">Contact Us</ContactHeading>

        <ContactSection>
          <TopGrid>
            {/* Left: Contact form */}
            <FormSection>
              <FormWrap onSubmit={handleSubmit}>
                <InputRow>
                  <InputGroup>
                    <Label>Name</Label>
                    <Input 
                      name="firstName" 
                      placeholder="" 
                      value={form.firstName} 
                      onChange={handleChange} 
                      data-reveal="left" 
                    />
                  </InputGroup>
                  <InputGroup>
                    <Label>Phone Number</Label>
                    <Input 
                      name="phone" 
                      placeholder="" 
                      value={form.phone} 
                      onChange={handleChange} 
                      data-reveal="left" 
                    />
                  </InputGroup>
                </InputRow>
                <InputGroup>
                  <Label>Address</Label>
                  <Input 
                    name="address" 
                    placeholder="" 
                    type="text" 
                    value={form.address} 
                    onChange={handleChange} 
                    data-reveal="left" 
                  />
                </InputGroup>
                <InputGroup>
                  <Label>Message</Label>
                  <TextArea 
                    name="message" 
                    placeholder="" 
                    value={form.message} 
                    onChange={handleChange} 
                    data-reveal="left" 
                  />
                </InputGroup>
                <SubmitButton type="submit" data-reveal="up">
                  SUBMIT
                </SubmitButton>
              </FormWrap>
            </FormSection>

            {/* Right: Contact info cards */}
            <InfoSection>
              <InfoCard data-reveal="right">
                <IconCircle>📞</IconCircle>
                <CardTitle>PHONE NUMBER</CardTitle>
                <CardText>
                  <LinkText href="tel:+923708025503">+92 370 8025503</LinkText>
                </CardText>
              </InfoCard>
              <InfoCard data-reveal="right">
                <IconCircle>✉️</IconCircle>
                <CardTitle>EMAIL</CardTitle>
                <CardText>
                  <LinkText href="mailto:fancyandflour@gmail.com">fancyandflour@gmail.com</LinkText>
                </CardText>
              </InfoCard>
              
              <InfoCard data-reveal="right">
                <IconCircle>📍</IconCircle>
                <CardTitle>LOCATION</CardTitle>
                <CardText>
                  Balochak Welfare Medical Center<br/>
                  Main Sahowala Rd, near Water Tank, تحصیل سمبڑیال, 54200, PK
                </CardText>
              </InfoCard>
            </InfoSection>
          </TopGrid>
        </ContactSection>

        {/* Bottom: Map - keeping it the same */}
        <MapSection data-reveal="zoom">
          <MapFrame
            title="Flour & Fancy Location"
            src="https://maps.google.com/maps?q=32.4613521,74.4120464&z=15&output=embed"
            loading="lazy"
          />
        </MapSection>
      </Container>
    </Page>
  );
};

export default ContactUs;